// File: downloadRenders - build a ZIP of every render of a car, organised by view
// Created: 2026-10-20
// Updated: 2026-10-20
// Author: Erick Hernández Silva
//
// Layout (folder per view, then per step in render order):
//   <Car>-renders/
//     README.txt, manifest.csv
//     View 1 - Exterior 3-4 Front/
//       03 Exterior paint/P1 - Tokyo Black Gloss.webp
//       14 Steering wheel/O1 - Original - Onyx.webp      (option - variant)
//       Overlays/Show frame.webp
//       Sceneries/Lake.webp, Lake - shadow.webp
//     Option thumbnails/03 Exterior paint/P1 - Tokyo Black Gloss.webp
//     Scenery thumbnails/Lake.webp
//     Model picker/<Car>.webp
// Images are fetched straight from S3 (public, CORS *) and stored uncompressed
// (they are already WebP/PNG/JPG), streaming into the ZIP to keep memory flat.

import { Zip, ZipPassThrough, strToU8 } from 'fflate';
import type { CarConfig, ViewImages, ViewKey } from './carWizardTypes';
import { VIEW_KEYS } from './carWizardTypes';
import { isRenderableImage } from './carModel';

const VIEW_NAMES: Record<ViewKey, string> = {
  view1: 'View 1 - Exterior 3-4 Front',
  view2: 'View 2 - Exterior 3-4 Rear',
  view3: 'View 3 - Wheel Closeup',
  view4: 'View 4 - Interior 3-4 Front',
  view5: 'View 5 - Top View',
  view6: 'View 6 - Front View',
};

interface Entry {
  path: string; // inside the root folder
  url: string;
  view: string;
  group: string; // step / overlay / scenery / thumbnail
  item: string; // option, overlay label, scenery name
  variant: string; // sub-option name, "shadow", ...
}

export interface RendersZipResult {
  fileName: string;
  total: number;
  downloaded: number;
  failed: Array<{ path: string; url: string; error: string }>;
}

/** Safe, readable file/folder name (Windows + macOS). */
const clean = (s: string | undefined, fallback = 'untitled') =>
  (s || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^[\s.-]+|[\s.]+$/g, '')
    .slice(0, 80) || fallback;

const extOf = (url: string) => {
  const m = url.split('?')[0].match(/\.([a-z0-9]{2,5})$/i);
  return m ? `.${m[1].toLowerCase()}` : '.webp';
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Every image of the car with its target path. Pure; exported for tests/counting. */
export const collectRenderEntries = (car: CarConfig): Entry[] => {
  const entries: Entry[] = [];
  const used = new Set<string>();
  const add = (e: Omit<Entry, 'path'> & { dir: string; name: string }) => {
    if (!isRenderableImage(e.url)) return;
    const ext = extOf(e.url);
    let path = `${e.dir}/${e.name}${ext}`;
    for (let i = 2; used.has(path.toLowerCase()); i++) path = `${e.dir}/${e.name} (${i})${ext}`;
    used.add(path.toLowerCase());
    entries.push({ path, url: e.url, view: e.view, group: e.group, item: e.item, variant: e.variant });
  };

  const perView = (images: ViewImages | undefined, dirIn: (view: string) => string, name: string, meta: Omit<Entry, 'path' | 'url' | 'view'>) =>
    VIEW_KEYS.forEach((vk) => add({ ...meta, url: images?.[vk] || '', view: VIEW_NAMES[vk], dir: dirIn(VIEW_NAMES[vk]), name }));

  // Steps, numbered by render order (lower number = further back).
  car.steps.forEach((step, i) => {
    const stepDir = `${pad(i + 1)} ${clean(step.stepName, step.stepId)}`;
    step.options.forEach((opt) => {
      const optName = `${clean(opt.optionId, 'option')} - ${clean(opt.optionName)}`;
      perView(opt.images, (v) => `${v}/${stepDir}`, optName, { group: step.stepName, item: opt.optionName, variant: '' });
      (opt.subOptions || []).forEach((sub) =>
        perView(sub.images, (v) => `${v}/${stepDir}`, `${optName} - ${clean(sub.optionName, sub.optionId)}`,
          { group: step.stepName, item: opt.optionName, variant: sub.optionName }));
      add({ url: opt.presentationImage || '', view: '', group: step.stepName, item: opt.optionName, variant: 'thumbnail',
        dir: `Option thumbnails/${stepDir}`, name: optName });
    });
  });

  (car.overlays || []).forEach((ov) =>
    perView(ov.images, (v) => `${v}/Overlays`, clean(ov.label, ov.overlayId), { group: 'Overlay', item: ov.label, variant: '' }));

  (car.sceneries || []).forEach((sc) => {
    const name = clean(sc.name, sc.sceneryId);
    perView(sc.images, (v) => `${v}/Sceneries`, name, { group: 'Scenery', item: sc.name, variant: '' });
    perView(sc.shadowImages, (v) => `${v}/Sceneries`, `${name} - shadow`, { group: 'Scenery', item: sc.name, variant: 'shadow' });
    add({ url: sc.thumbnail || '', view: '', group: 'Scenery', item: sc.name, variant: 'thumbnail', dir: 'Scenery thumbnails', name });
  });

  add({ url: car.splash?.image || '', view: '', group: 'Model picker', item: car.carName, variant: '', dir: 'Model picker', name: clean(car.carName) });
  return entries;
};

const csvCell = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/**
 * Download every render of `car` as one ZIP. `onProgress(done, total)` fires as
 * files land. Fails soft: unreachable images are listed in missing.txt.
 */
export async function downloadRendersZip(
  car: CarConfig,
  onProgress?: (done: number, total: number) => void,
  concurrency = 6,
): Promise<RendersZipResult> {
  const entries = collectRenderEntries(car);
  const root = `${clean(car.carName, 'car')}-renders`;
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `${root}-${stamp}.zip`;

  const chunks: Uint8Array[] = [];
  let zipError: Error | null = null;
  const zip = new Zip((err, data) => {
    if (err) { zipError = err; return; }
    chunks.push(data);
  });
  const addFile = (path: string, data: Uint8Array) => {
    const f = new ZipPassThrough(`${root}/${path}`); // stored: images are already compressed
    zip.add(f);
    f.push(data, true);
  };

  // Same URL used in several places is fetched once.
  const cache = new Map<string, Promise<Uint8Array>>();
  const fetchBytes = (url: string) => {
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { mode: 'cors', cache: 'no-store' }).then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return new Uint8Array(await r.arrayBuffer());
      }));
    }
    return cache.get(url)!;
  };

  const failed: RendersZipResult['failed'] = [];
  let done = 0;
  onProgress?.(0, entries.length);

  // Fetch in parallel, but write to the ZIP in entry order (stable listing).
  const results: Array<Uint8Array | null> = new Array(entries.length).fill(null);
  let next = 0;
  const worker = async () => {
    while (next < entries.length) {
      const i = next++;
      const e = entries[i];
      try {
        results[i] = await fetchBytes(e.url);
      } catch (err) {
        failed.push({ path: e.path, url: e.url, error: (err as Error).message || 'fetch failed' });
      }
      onProgress?.(++done, entries.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, worker));

  entries.forEach((e, i) => { if (results[i]) addFile(e.path, results[i]!); });

  const manifest = [
    ['file', 'view', 'step / group', 'option / item', 'variant', 'url'].map(csvCell).join(','),
    ...entries.map((e) => [e.path, e.view, e.group, e.item, e.variant, e.url].map(csvCell).join(',')),
  ].join('\r\n');
  addFile('manifest.csv', strToU8('﻿' + manifest)); // BOM so Excel/Sheets read UTF-8

  addFile('README.txt', strToU8([
    `${car.carName} — renders (${stamp})`,
    '',
    'One folder per camera view. Inside each view, one folder per step,',
    'numbered in render order (01 = back layer, higher numbers draw on top).',
    'File names: "<option id> - <option name>[ - <variant>]".',
    'Overlays (show frame, soft top, ...) and Sceneries have their own folder per view.',
    '"Option thumbnails" holds the small images shown on each option card.',
    'manifest.csv lists every file with its view, step, option, variant and source URL.',
    failed.length ? `\n${failed.length} image(s) could not be downloaded — see missing.txt.` : '',
  ].join('\r\n')));

  if (failed.length) {
    addFile('missing.txt', strToU8(failed.map((f) => `${f.path}\t${f.error}\t${f.url}`).join('\r\n')));
  }

  zip.end();
  if (zipError) throw zipError;

  const blob = new Blob(chunks as BlobPart[], { type: 'application/zip' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60_000);

  return { fileName, total: entries.length, downloaded: entries.length - failed.length, failed };
}
